import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { apiService, type UserSettings } from '../services/api';
import { logger } from '../utils/logger';
import { useAuth } from '../hooks/useAuth';

export type UserSettingsContextType = {
  settings: UserSettings;
  loading: boolean;
  updateSettings: (updates: Partial<UserSettings>) => Promise<void>;
  setSettingsFromFetch: (fetchedSettings: UserSettings) => void;
};

const defaultSettings: UserSettings = {
  videoDisabled: false,
  stickyMiniPlayerDisabled: false,
};

export const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const storageKey = user?.id ? `user-settings:${user.id}` : 'user-settings:guest';

  useEffect(() => {
    if (!isAuthenticated) {
      setSettings(defaultSettings);
      setLoading(false);
    }
  }, [isAuthenticated]);

  const setSettingsFromFetch = useCallback((fetchedSettings: UserSettings) => {
    setSettings(fetchedSettings);
    if (typeof window !== 'undefined' && user?.id) {
      const storageKey = `user-settings:${user.id}`;
      window.localStorage.setItem(storageKey, JSON.stringify(fetchedSettings));
    }
    setLoading(false);
  }, [user?.id]);

  const updateSettings = useCallback(async (updates: Partial<UserSettings>) => {
    if (typeof window === 'undefined') return;

    const next = { ...settings, ...updates };
    setSettings(next);
    window.localStorage.setItem(storageKey, JSON.stringify(next));

    if (!isAuthenticated) return;

    try {
      await apiService.updateUserSettings(updates);
    } catch (error) {
      logger.warn('Failed to update user settings', error);
    }
  }, [settings, isAuthenticated, storageKey]);

  const value = useMemo(
    () => ({
      settings,
      loading,
      updateSettings,
      setSettingsFromFetch,
    }),
    [settings, loading, updateSettings, setSettingsFromFetch]
  );

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}
